import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { EyeCheckboxButton, PenCheckboxButton } from '../CheckboxButton.tsx';
import type { MeasurementProps } from './types';
import * as DT from '@/engine/PlanningAlgorithm/DataTypes';
import type { ViewsService } from '@/engine/app/Views.service';
import { useInjection } from 'inversify-react';
import { TYPES } from '@/engine/ioc';
import { useTheme } from '@mui/material/styles';
import { UpdatableResetIconButton } from '../BoxTools';
import { Button, IconButton, Stack, Tooltip } from '@mui/material';
import styled from 'styled-components';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import type { AreaDiametersController } from './AreaDiameters.controller';
import AddIcon from '@mui/icons-material/Add';
import { EditableCellTextField } from '../../common/EditableCellTextField';
import type { BasicBoxProps } from './types';
import { useHighlight } from '../hooks/useHighlight';
import { useSelect } from '../hooks/useSelect';
import { UpdatableValue } from '../common/UpdatableValue.tsx';
import { getConditionalStyles } from '../styles';
import type { MeasurementBoxesController } from '.';

type AreaDiameterMeasurementBoxProps = BasicBoxProps<MeasurementProps>;

const getSplittedDiameters = function (
  customDiameterDescriptor: string,
  diameters: Array<MeasurementProps>
): [MeasurementProps, Array<MeasurementProps>] {
  let customDiameter: MeasurementProps | undefined = undefined;
  diameters = diameters.filter((diameter) => {
    if (diameter.descriptor === customDiameterDescriptor) {
      customDiameter = diameter;
      return false;
    } else {
      return true;
    }
  });
  if (customDiameter === undefined) {
    throw new Error('Custom diameter is undefined');
  }
  return [customDiameter, diameters];
};

export const AreaDiameterMeasurementBox: React.FC<AreaDiameterMeasurementBoxProps> = (props) => {
  const theme = useTheme();

  const mainController = useInjection<MeasurementBoxesController>(TYPES.IMeasurementsBoxesController);
  const controller = useInjection<AreaDiametersController>(TYPES.AreaDiametersController);
  const customDiameterDescriptor = controller.getCustomDiameterDescriptor();
  if (customDiameterDescriptor === undefined) {
    throw new Error('Custom diameter is undefined');
  }

  const viewsService = useInjection<ViewsService>(TYPES.ViewsService);
  const measurementVMs = viewsService.currentView?.getMeasurements();

  const { isHighlighted } = useHighlight(measurementVMs);
  const { isSelected } = useSelect(measurementVMs);

  const [customDiameterProps, diameterMeasurements] = getSplittedDiameters(
    customDiameterDescriptor,
    props.measurements
  );

  return (
    <Container>
      <TableContainer component={Paper}>
        <Table aria-label="a dense table" size="small">
          <TableHead>
            <TableRow key={'header'} sx={{ '& th': { fontSize: '0.7em', paddingLeft: 0.2 } }}>
              <TableCell></TableCell>
              <TableCell></TableCell>
              <TableCell align="right" sx={{ fontWeight: 600, width: '3em' }}>
                Mean&nbsp;Ø
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  fontWeight: 600,
                  paddingRight: '1.3em',
                  width: '8em',
                  paddingLeft: '0em'
                }}
              >
                Area
              </TableCell>
              <TableCell
                align="right"
                sx={{
                  '&': { paddingRight: 0.2, paddingLeft: 0, width: '2em' }
                }}
              ></TableCell>
              <TableCell
                align="right"
                sx={{
                  '&': { paddingRight: 0.2, paddingLeft: 0, width: '2em' }
                }}
              ></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {diameterMeasurements.map((measurement: MeasurementProps, index) => {
              const isItemSelected: boolean = isSelected(measurement.descriptor);
              const isItemHighlighted: boolean = isHighlighted(measurement.descriptor);
              const isItemUnavailable = !measurementVMs?.find((mvm) => mvm.descriptor === measurement.descriptor)
                ?.isAvailable;
              if (isItemUnavailable) return null;
              return (
                <TableRow
                  key={measurement.label + index}
                  data-testid={measurement.descriptor + 'AreaDiameter'}
                  sx={{
                    '&:last-child td, &:last-child th': { border: 0 },
                    '& td:first-child': {
                      paddingLeft: '1em'
                    },
                    ...getConditionalStyles(
                      isItemHighlighted,
                      theme,
                      {
                        paddingLeft: 0.3,
                        paddingTop: 0,
                        paddingBottom: 0
                      },
                      {
                        paddingRight: 0.5,
                        paddingTop: 0,
                        paddingBottom: 0,
                        width: '5.85rem'
                      }
                    )
                  }}
                  onMouseEnter={(): void => mainController.setMeasurementHighlighting(measurement.descriptor, true)}
                  onMouseLeave={(): void => mainController.setMeasurementHighlighting(measurement.descriptor, false)}
                  selected={isItemSelected}
                  onClick={(): void => {
                    mainController.setMeasurementSelection(measurement.descriptor, !isItemSelected);
                  }}
                >
                  <TableCell key={'label' + index} sx={{ paddingLeft: '1em' }}>
                    {measurement.imported ? (
                      <span>{measurement.label}</span>
                    ) : (
                      <EditableCellTextField
                        value={measurement.label}
                        onBlur={(e): void => {
                          controller.setDiameterLabel(measurement.descriptor, e.currentTarget.value);
                        }}
                      />
                    )}
                  </TableCell>
                  <StyledTableCell className="limited-width light-fill" sx={{ '&': { width: '3em' } }}>
                    <UpdatableResetIconButton descriptor={measurement.descriptor} controller={mainController} />
                  </StyledTableCell>
                  <TableCell align="right" key={'mean' + index} sx={{ '&': { paddingRight: '1em' } }}>
                    <UpdatableValue
                      descriptor={measurement.descriptor}
                      constructor={DT.AreaMeasurement}
                      valueKey={'dMean'}
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    key={'area' + index}
                    sx={{
                      '&': { paddingRight: '0.5em', paddingLeft: '0em' }
                    }}
                  >
                    <UpdatableValue
                      descriptor={measurement.descriptor}
                      constructor={DT.AreaMeasurement}
                      valueKey={'area'}
                    />{' '}
                    mm²
                  </TableCell>
                  <StyledTableCell
                    align="right"
                    key={'deleteOrEdit' + index}
                    className="limited-width"
                    onClick={(event): void => event.stopPropagation()}
                  >
                    {measurement.imported ? (
                      measurement.beingEdited !== undefined ? (
                        <PenCheckboxButton
                          onChange={(checked): void => {
                            mainController.setMeasurementBeingEdited(measurement.descriptor, checked);
                          }}
                          checked={measurement.beingEdited}
                          key={'tunabilityBtn' + index}
                        />
                      ) : (
                        <div />
                      )
                    ) : (
                      <Tooltip title="Delete" placement="left">
                        <span>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(): void => {
                              // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
                              controller.deleteDiameterByDescriptor(measurement.descriptor);
                            }}
                          >
                            <DeleteOutlinedIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </StyledTableCell>
                  <StyledTableCell
                    align="right"
                    key={'visibility' + index}
                    className="limited-width"
                    onClick={(event): void => event.stopPropagation()}
                  >
                    <EyeCheckboxButton
                      onChange={(checked): void =>
                        mainController.setMeasurementVisibility(measurement.descriptor, checked)
                      }
                      checked={measurement.visible}
                    />
                  </StyledTableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" spacing={1} justifyContent="center" sx={{ '&': { marginTop: 2 } }}>
        <TableContainer component={Paper}>
          <Table aria-label="a dense table" size="small">
            <TableBody>
              <TableRow
                sx={{
                  '&:last-child td, &:last-child th': { border: 0 },
                  '& td:first-child': { paddingLeft: '1em' },
                  ...getConditionalStyles(isHighlighted(customDiameterProps.descriptor), theme, {
                    paddingTop: '0.6em',
                    paddingBottom: '0.6em'
                  })
                }}
                onMouseEnter={(): void =>
                  mainController.setMeasurementHighlighting(customDiameterProps.descriptor, true)
                }
                onMouseLeave={(): void =>
                  mainController.setMeasurementHighlighting(customDiameterProps.descriptor, false)
                }
                selected={isSelected(customDiameterProps.descriptor)}
                onClick={(): void =>
                  mainController.setMeasurementSelection(
                    customDiameterProps.descriptor,
                    !isSelected(customDiameterProps.descriptor)
                  )
                }
              >
                <TableCell sx={{ width: '7em' }}>{customDiameterProps.label}</TableCell>
                <TableCell
                  align="right"
                  sx={{
                    '&': {
                      paddingRight: '.3em',
                      paddingLeft: '.3em',
                      width: '4em'
                    }
                  }}
                >
                  <UpdatableValue
                    descriptor={customDiameterProps.descriptor}
                    constructor={DT.AreaMeasurement}
                    valueKey={'dMean'}
                  />
                </TableCell>
                <TableCell
                  align="right"
                  sx={{
                    '&': {
                      paddingRight: '.8em',
                      paddingLeft: '.3em',
                      width: '7em'
                    }
                  }}
                >
                  <UpdatableValue
                    descriptor={customDiameterProps.descriptor}
                    constructor={DT.AreaMeasurement}
                    valueKey={'area'}
                  />{' '}
                  mm²
                </TableCell>
                <StyledTableCell
                  align="right"
                  key={'beingEditedCustom'}
                  className="limited-width"
                  onClick={(event): void => event.stopPropagation()}
                >
                  {customDiameterProps.beingEdited !== undefined ? (
                    <PenCheckboxButton
                      onChange={(checked): void => {
                        mainController.setMeasurementBeingEdited(customDiameterProps.descriptor, checked);
                      }}
                      checked={customDiameterProps.beingEdited}
                      key={'tunabilityBtnCustom'}
                    />
                  ) : (
                    <div />
                  )}
                </StyledTableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        <Button
          startIcon={<AddIcon />}
          variant="contained"
          color="primary"
          onClick={(): void => {
            // eslint-disable-next-line @typescript-eslint/no-floating-promises -- //TODO: [chebotarev] fix eslint no-floating-promises
            controller.addDiameter('Custom', customDiameterProps.descriptor);
          }}
        >
          ADD
        </Button>
      </Stack>
    </Container>
  );
};

const Container = styled.div``;

export const StyledTableCell = styled(TableCell)`
  &.limited-width {
    padding-right: 0;
    padding-left: 0;
  }
`;
